/**
 * Product page as a layer OVER the list it was opened from.
 *
 * Opening a product from Home or Saved navigates with
 * `state.background = <the list's location>`. App then keeps rendering the
 * list for that background location and draws the product page on top of it
 * (see App.tsx). Going back — the in-app arrow, the Android back button or
 * gesture, the browser back button — just removes the layer: the list was
 * never unmounted, so it is exactly where the dealer left it (scroll offset,
 * tab, search and filters). No timing-sensitive scroll restoration involved.
 *
 * A product reached any other way (push notification, deep link, a reload on
 * /product/:id) has no background and renders as a normal page, as before.
 */
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate, type Location } from 'react-router-dom';
import { ROUTES } from './paths';

export interface ProductOverlayState {
  /** The list location the product page was opened over. */
  background?: Location;
}

/** The background location carried in the current history entry, if any. */
export function backgroundOf(location: Location): Location | undefined {
  return (location.state as ProductOverlayState | null)?.background;
}

/**
 * Open a product over the current screen. From inside an open product page
 * (the "More in …" strip) the existing background is kept, so backing out of
 * the whole chain still lands on the original list.
 */
export function useOpenProduct(): (productId: string) => void {
  const navigate = useNavigate();
  const location = useLocation();
  return (productId: string) =>
    navigate(ROUTES.product(productId), {
      state: { background: backgroundOf(location) ?? location } satisfies ProductOverlayState,
    });
}

const OverlayOpenContext = createContext(false);

/** True while a product page is drawn over the list (the list is covered). */
export function useProductOverlayOpen(): boolean {
  return useContext(OverlayOpenContext);
}

export function ProductOverlayProvider({
  open,
  children,
}: {
  open: boolean;
  children: ReactNode;
}): JSX.Element {
  return <OverlayOpenContext.Provider value={open}>{children}</OverlayOpenContext.Provider>;
}

/**
 * Full-screen layer for the product page. While it is up, the page behind it
 * must not scroll (Saved scrolls the document itself): lock the root's
 * overflow, which keeps the list's scroll offset intact, and unlock on close.
 */
export function ProductOverlay({ children }: { children: ReactNode }): JSX.Element {
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => {
      root.style.overflow = prev;
    };
  }, []);
  return (
    <div className="fixed inset-0 z-40 mx-auto flex w-full max-w-2xl flex-col overscroll-contain bg-ground">
      {children}
    </div>
  );
}
